import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { addonsApi } from '@/api/addons';
import { licensingApi } from '@/api/licensing';
import { LIMIT_KEYS, LIMIT_LABELS } from '@/types/licensing';
import { formatNIS } from '@/utils/currency';
import type { AddonCatalogItem, System } from '@/types';

const UNIT_OPTIONS = ['device', 'extension', 'GB', 'seat', 'SMS', 'branch'] as const;

type FormState = {
  code: string;
  system_code: string;
  name: string;
  description: string;
  unit: string;
  price_monthly_nis: string;
  price_yearly_nis: string;
  price_onetime_nis: string;
  increments_limit_key: string;
  is_active: boolean;
  sort_order: string;
};

const emptyForm: FormState = {
  code: '',
  system_code: '',
  name: '',
  description: '',
  unit: 'device',
  price_monthly_nis: '',
  price_yearly_nis: '',
  price_onetime_nis: '',
  increments_limit_key: '',
  is_active: true,
  sort_order: '0',
};

function agorotToNisInput(value: number | null): string {
  if (value === null || value === undefined) return '';
  return String(value / 100);
}

function nisInputToAgorot(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return Math.round(Number(trimmed) * 100);
}

function itemToForm(item: AddonCatalogItem): FormState {
  return {
    code: item.code,
    system_code: item.system_code ?? '',
    name: item.name,
    description: item.description ?? '',
    unit: item.unit,
    price_monthly_nis: agorotToNisInput(item.price_monthly_agorot),
    price_yearly_nis: agorotToNisInput(item.price_yearly_agorot),
    price_onetime_nis: agorotToNisInput(item.price_onetime_agorot),
    increments_limit_key: item.increments_limit_key ?? '',
    is_active: item.is_active,
    sort_order: String(item.sort_order),
  };
}

function formToPayload(form: FormState): Omit<AddonCatalogItem, 'created_at'> {
  return {
    code: form.code.trim(),
    system_code: form.system_code || null,
    name: form.name.trim(),
    description: form.description.trim() || null,
    unit: form.unit,
    price_monthly_agorot: nisInputToAgorot(form.price_monthly_nis),
    price_yearly_agorot: nisInputToAgorot(form.price_yearly_nis),
    price_onetime_agorot: nisInputToAgorot(form.price_onetime_nis),
    increments_limit_key: form.increments_limit_key || null,
    is_active: form.is_active,
    sort_order: Number(form.sort_order || 0),
  };
}

function formatPrice(value: number | null): string {
  return value === null ? '-' : formatNIS(value);
}

interface AddonModalProps {
  item: AddonCatalogItem | null;
  systems: System[];
  onClose: () => void;
  onSaved: () => void;
}

function AddonModal({ item, systems, onClose, onSaved }: AddonModalProps) {
  const [form, setForm] = useState<FormState>(item ? itemToForm(item) : emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const qc = useQueryClient();

  const createMutation = useMutation({
    mutationFn: addonsApi.createCatalogItem,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['addon-catalog'] });
      toast.success('Add-on created');
      onSaved();
    },
    onError: (error: any) => toast.error(error.message ?? 'Failed to create add-on'),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Partial<AddonCatalogItem>) => addonsApi.updateCatalogItem(item!.code, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['addon-catalog'] });
      toast.success('Add-on updated');
      onSaved();
    },
    onError: (error: any) => toast.error(error.message ?? 'Failed to update add-on'),
  });

  const validate = () => {
    const nextErrors: Record<string, string> = {};

    if (!form.code.trim()) nextErrors.code = 'Required';
    if (!form.name.trim()) nextErrors.name = 'Required';
    if (!form.unit.trim()) nextErrors.unit = 'Required';
    if (form.sort_order && Number.isNaN(Number(form.sort_order))) nextErrors.sort_order = 'Must be a number';

    for (const key of ['price_monthly_nis', 'price_yearly_nis', 'price_onetime_nis'] as const) {
      const value = form[key].trim();
      if (value && Number.isNaN(Number(value))) {
        nextErrors[key] = 'Must be a number';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload = formToPayload(form);
    if (item) {
      const { code: _code, created_at: _createdAt, ...patch } = payload as AddonCatalogItem;
      updateMutation.mutate(patch);
      return;
    }
    createMutation.mutate(payload);
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-2xl rounded-xl p-6 space-y-4 overflow-y-auto max-h-[90vh]"
        style={{ background: '#1a1d27', border: '1px solid #2d3148' }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-white">{item ? `Edit Add-on - ${item.name}` : 'Add Add-on'}</h2>
            {item && <code className="text-[11px]" style={{ color: '#6b7280' }}>{item.code}</code>}
          </div>
          <button onClick={onClose} style={{ color: '#9ca3af' }}><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-gray-400">Code</label>
            <input
              value={form.code}
              onChange={e => setForm(prev => ({ ...prev, code: e.target.value }))}
              disabled={!!item}
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none font-mono disabled:opacity-60"
              style={{ background: '#252836', border: `1px solid ${errors.code ? '#ef4444' : '#2d3148'}` }}
            />
            {errors.code && <p className="text-xs text-red-400">{errors.code}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-400">System</label>
            <select
              value={form.system_code}
              onChange={e => setForm(prev => ({ ...prev, system_code: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: '1px solid #2d3148' }}
            >
              <option value="">All systems</option>
              {systems.map(system => (
                <option key={system.code} value={system.code}>{system.name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-400">Name</label>
            <input
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: `1px solid ${errors.name ? '#ef4444' : '#2d3148'}` }}
            />
            {errors.name && <p className="text-xs text-red-400">{errors.name}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-400">Unit</label>
            <select
              value={form.unit}
              onChange={e => setForm(prev => ({ ...prev, unit: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: `1px solid ${errors.unit ? '#ef4444' : '#2d3148'}` }}
            >
              {UNIT_OPTIONS.map(unit => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </select>
            {errors.unit && <p className="text-xs text-red-400">{errors.unit}</p>}
          </div>

          <div className="space-y-1 sm:col-span-2">
            <label className="text-xs text-gray-400">Description</label>
            <input
              value={form.description}
              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: '1px solid #2d3148' }}
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-400">Monthly Price (NIS)</label>
            <input
              value={form.price_monthly_nis}
              onChange={e => setForm(prev => ({ ...prev, price_monthly_nis: e.target.value }))}
              type="number"
              min="0"
              step="0.01"
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: `1px solid ${errors.price_monthly_nis ? '#ef4444' : '#2d3148'}` }}
            />
            {errors.price_monthly_nis && <p className="text-xs text-red-400">{errors.price_monthly_nis}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-400">Yearly Price (NIS)</label>
            <input
              value={form.price_yearly_nis}
              onChange={e => setForm(prev => ({ ...prev, price_yearly_nis: e.target.value }))}
              type="number"
              min="0"
              step="0.01"
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: `1px solid ${errors.price_yearly_nis ? '#ef4444' : '#2d3148'}` }}
            />
            {errors.price_yearly_nis && <p className="text-xs text-red-400">{errors.price_yearly_nis}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-400">One-time Price (NIS)</label>
            <input
              value={form.price_onetime_nis}
              onChange={e => setForm(prev => ({ ...prev, price_onetime_nis: e.target.value }))}
              type="number"
              min="0"
              step="0.01"
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: `1px solid ${errors.price_onetime_nis ? '#ef4444' : '#2d3148'}` }}
            />
            {errors.price_onetime_nis && <p className="text-xs text-red-400">{errors.price_onetime_nis}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-400">Increments Limit</label>
            <select
              value={form.increments_limit_key}
              onChange={e => setForm(prev => ({ ...prev, increments_limit_key: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: '1px solid #2d3148' }}
            >
              <option value="">None</option>
              {LIMIT_KEYS.map(key => (
                <option key={key} value={key}>{LIMIT_LABELS[key].label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-400">Sort Order</label>
            <input
              value={form.sort_order}
              onChange={e => setForm(prev => ({ ...prev, sort_order: e.target.value }))}
              type="number"
              step="1"
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: `1px solid ${errors.sort_order ? '#ef4444' : '#2d3148'}` }}
            />
            {errors.sort_order && <p className="text-xs text-red-400">{errors.sort_order}</p>}
          </div>

          <div className="flex items-center gap-2 pt-6">
            <input
              id="addon-active"
              type="checkbox"
              checked={form.is_active}
              onChange={e => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
              className="rounded border-gray-600"
            />
            <label htmlFor="addon-active" className="text-sm text-gray-300">Active</label>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white transition-colors"
            style={{ background: '#252836', border: '1px solid #2d3148' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
            style={{ background: '#6366f1' }}
          >
            {isPending ? 'Saving...' : item ? 'Save Changes' : 'Create Add-on'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AddonsPage() {
  const qc = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [editingItem, setEditingItem] = useState<AddonCatalogItem | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['addon-catalog', showInactive],
    queryFn: () => addonsApi.listCatalog(showInactive),
  });

  const { data: systems = [] } = useQuery({
    queryKey: ['licensing-systems'],
    queryFn: licensingApi.listSystems,
  });

  const systemMap = systems.reduce<Record<string, string>>((acc, system) => {
    acc[system.code] = system.name;
    return acc;
  }, {});

  const toggleMutation = useMutation({
    mutationFn: ({ code, is_active }: { code: string; is_active: boolean }) =>
      addonsApi.updateCatalogItem(code, { is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['addon-catalog'] });
      toast.success('Add-on updated');
    },
    onError: (error: any) => toast.error(error.message ?? 'Failed to update add-on'),
  });

  const deleteMutation = useMutation({
    mutationFn: addonsApi.deleteCatalogItem,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['addon-catalog'] });
      toast.success('Add-on deleted');
    },
    onError: (error: any) => toast.error(error.message ?? 'Failed to delete add-on'),
  });

  const handleDelete = (item: AddonCatalogItem) => {
    if (window.confirm(`Delete add-on "${item.name}"? This cannot be undone.`)) {
      deleteMutation.mutate(item.code);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Add-on Catalog</h1>
          <p className="text-sm mt-1" style={{ color: '#9ca3af' }}>
            Manage paid add-ons that can be sold on top of plans.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-white transition-colors"
          style={{ background: '#6366f1' }}
        >
          <Plus size={13} /> Add Add-on
        </button>
      </div>

      <div className="flex items-center gap-2 text-xs" style={{ color: '#9ca3af' }}>
        <input
          id="addons-show-inactive"
          type="checkbox"
          checked={showInactive}
          onChange={e => setShowInactive(e.target.checked)}
          className="rounded border-gray-600"
        />
        <label htmlFor="addons-show-inactive">Show inactive add-ons</label>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2" style={{ borderColor: '#6366f1' }} />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl p-8 text-center text-sm" style={{ background: '#1a1d27', border: '1px solid #2d3148', color: '#6b7280' }}>
          No add-ons configured.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid #2d3148' }}>
                {['Code', 'System', 'Name', 'Unit', 'Monthly NIS', 'Yearly NIS', 'One-time NIS', 'Increments', 'Active', 'Actions'].map(header => (
                  <th key={header} className="text-left px-4 py-3 text-xs font-medium" style={{ color: '#6b7280' }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.code} style={{ borderBottom: '1px solid #2d314840' }}>
                  <td className="px-4 py-3 font-mono text-xs text-white">{item.code}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: '#9ca3af' }}>
                    {item.system_code ? (systemMap[item.system_code] ?? item.system_code) : 'All systems'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{item.name}</div>
                    {item.description && (
                      <div className="text-[11px]" style={{ color: '#6b7280' }}>{item.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-white">{item.unit}</td>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: '#e2e8f0' }}>{formatPrice(item.price_monthly_agorot)}</td>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: '#9ca3af' }}>{formatPrice(item.price_yearly_agorot)}</td>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: '#9ca3af' }}>{formatPrice(item.price_onetime_agorot)}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: '#9ca3af' }}>
                    {item.increments_limit_key ? LIMIT_LABELS[item.increments_limit_key as keyof typeof LIMIT_LABELS]?.label ?? item.increments_limit_key : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleMutation.mutate({ code: item.code, is_active: !item.is_active })}
                      className="relative flex-shrink-0 w-9 h-5 rounded-full transition-all duration-200"
                      style={{ background: item.is_active ? '#4ade8060' : '#2d3148' }}
                    >
                      <span
                        className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
                        style={{ transform: item.is_active ? 'translateX(16px)' : 'translateX(0)' }}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingItem(item)}
                        title="Edit add-on"
                        className="p-1.5 rounded-lg transition-colors"
                        style={{ color: '#9ca3af' }}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        disabled={deleteMutation.isPending}
                        title="Delete add-on"
                        className="p-1.5 rounded-lg transition-colors disabled:opacity-40"
                        style={{ color: '#9ca3af' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <AddonModal
          item={null}
          systems={systems}
          onClose={() => setShowCreate(false)}
          onSaved={() => setShowCreate(false)}
        />
      )}

      {editingItem && (
        <AddonModal
          item={editingItem}
          systems={systems}
          onClose={() => setEditingItem(null)}
          onSaved={() => setEditingItem(null)}
        />
      )}
    </div>
  );
}
